/**
 * ReviewCard - Content review interface (Like/Dislike)
 * Shows at end of content with auto-dismiss timer
 */
import React, { useEffect, useState, useRef } from 'react';
import { useFocusable, updateAllLayouts, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import '../styles/reviewCard.css';

/**
 * ReviewCard Component
 * @param {Object} props
 * @param {string} props.coverImageUrl - URL of content cover image
 * @param {string} props.contentType - Content type (movie, series, program)
 * @param {string} props.title - Content title
 * @param {number} props.season - Season number (for episodes)
 * @param {number} props.episode - Episode number (for episodes)
 * @param {boolean} props.visible - Whether card should be shown
 * @param {Function} props.onSubmit - Callback when review is submitted (reviewType: 1|2)
 * @param {Function} props.onDismiss - Callback when card is dismissed
 * @param {number} props.displayDuration - Duration to show card in seconds (default 15)
 */
export default function ReviewCard(props) {
    var coverImageUrl = props.coverImageUrl;
    var visible = props.visible;
    var onSubmit = props.onSubmit;
    var onDismiss = props.onDismiss;
    var displayDuration = props.displayDuration || 10;

    var [focusedButton, setFocusedButton] = useState('like'); // 'like' or 'dislike'
    var [progress, setProgress] = useState(1.0); // 1.0 = full, 0.0 = empty
    var [isAnimating, setIsAnimating] = useState(false);

    var dismissTimerRef = useRef(null);
    var progressIntervalRef = useRef(null);

    // Focus management
    var { ref, focusSelf, focusKey } = useFocusable({
        focusKey: 'reviewCard',
        focusable: visible,
        preferredChildFocusKey: 'reviewLikeBtn',
    });

    // Handle visibility changes
    useEffect(function () {
        if (visible) {
            // Fade in when becoming visible
            var timer = setTimeout(function () {
                setIsAnimating(true);
            }, 50);

            // Focus card when visible
            focusSelf();

            // Update spatial navigation tree
            updateAllLayouts();

            return function () {
                clearTimeout(timer);
            };
        } else {
            setIsAnimating(false);
            setProgress(1.0);
        }
    }, [visible, focusSelf]);

    // Auto-dismiss timer and progress animation - only when visible
    useEffect(function () {
        if (!visible) return;

        var startTime = Date.now();
        var durationMs = displayDuration * 1000;

        // Progress update interval (every 100ms for smooth animation)
        progressIntervalRef.current = setInterval(function () {
            var elapsed = Date.now() - startTime;
            var remaining = Math.max(0, durationMs - elapsed);
            var newProgress = remaining / durationMs;
            setProgress(newProgress);
        }, 100);

        // Dismiss timer
        dismissTimerRef.current = setTimeout(function () {
            handleDismiss();
        }, durationMs);

        return function () {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
            }
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, [visible, displayDuration]);

    var handleDismiss = function () {
        setIsAnimating(false);
        setTimeout(function () {
            if (onDismiss) onDismiss();
        }, 400); // Match fade-out duration
    };

    var handleSubmit = function (reviewType) {
        if (onSubmit) {
            onSubmit(reviewType);
        }
        handleDismiss();
    };


    // Global keyboard handlers - only when visible
    useEffect(function () {
        if (!visible) return;

        var handleKeyDown = function (e) {
            var key = e.key;
            var keyCode = e.keyCode;

            // Left/Right - Toggle between buttons
            if (key === 'ArrowLeft' || keyCode === 37) {
                e.preventDefault();
                setFocusedButton(focusedButton === 'like' ? 'dislike' : 'like');
            } else if (key === 'ArrowRight' || keyCode === 39) {
                e.preventDefault();
                setFocusedButton(focusedButton === 'like' ? 'dislike' : 'like');
            }
            // Enter/Select - Submit review
            else if (key === 'Enter' || keyCode === 13) {
                e.preventDefault();
                handleSubmit(focusedButton === 'like' ? 1 : 2);
            }
            // Escape/Back - Dismiss
            else if (
                key === 'Escape' || keyCode === 27 ||
                key === 'Backspace' || keyCode === 8 ||
                key === 'XF86Back' || keyCode === 10009 || // Tizen
                keyCode === 461 // WebOS
            ) {
                e.preventDefault();
                handleDismiss();
            }
            // Up/Down - Consume to prevent focus leak
            else if (key === 'ArrowUp' || keyCode === 38 || key === 'ArrowDown' || keyCode === 40) {
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return function () {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [visible, focusedButton, displayDuration]);

    // Always render FocusContext.Provider, only hide content conditionally
    return React.createElement(FocusContext.Provider, { value: focusKey },
        visible ? React.createElement('div', {
            ref: ref,
            className: 'mnb-review-card ' + (isAnimating ? 'visible' : ''),
        },
            // Cover Image
            coverImageUrl ? React.createElement('div', {
                className: 'mnb-review-cover',
            },
                React.createElement('img', {
                    src: coverImageUrl,
                    alt: 'Cover',
                    className: 'mnb-review-cover-img',
                })
            ) : null,

            // Buttons Section
            React.createElement('div', {
                className: 'mnb-review-buttons',
            },
                React.createElement(ReviewButton, {
                    id: 'like',
                    label: 'پسندیدم',
                    icon: '',
                    focused: focusedButton === 'like',
                    color: '#10B981',
                    onClick: function () { handleSubmit(1); },
                    focusable: visible,
                }),

                React.createElement('div', {
                    className: 'mnb-review-divider',
                }),

                React.createElement(ReviewButton, {
                    id: 'dislike',
                    label: 'نپسندیدم',
                    icon: '',
                    focused: focusedButton === 'dislike',
                    color: '#EF4444',
                    onClick: function () { handleSubmit(2); },
                    focusable: visible,
                })
            ),

            // Progress Bar
            React.createElement('div', {
                className: 'mnb-review-progress-container',
            },
                React.createElement('div', {
                    className: 'mnb-review-progress-bar',
                    style: {
                        width: (progress * 100) + '%',
                    },
                })
            )
        ) : null
    );
}

/**
 * ReviewButton - Individual button component
 */
function ReviewButton(props) {
    var id = props.id;
    var label = props.label;
    var icon = props.icon;
    var focused = props.focused;
    var color = props.color;
    var onClick = props.onClick;
    var focusable = props.focusable !== false;

    var { ref } = useFocusable({
        focusKey: 'review' + id.charAt(0).toUpperCase() + id.slice(1) + 'Btn',
        focusable: focusable,
        onEnterPress: onClick,
    });

    return React.createElement('button', {
        ref: ref,
        className: 'mnb-review-btn ' + (focused ? 'focused' : ''),
        onClick: onClick,
        style: focused ? {
            backgroundColor: color,
        } : {},
        type: 'button',
    },
        React.createElement('span', {
            className: 'mnb-review-btn-label',
        }, label),
        React.createElement('span', {
            className: 'mnb-review-btn-icon',
        }, icon)
    );
}
